import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  fetchAuthorArticles,
  getAuthorRouteDraft,
  reserveAuthorSeat,
  saveAuthorRouteDraft,
  submitAuthorInquiry,
} from '../api';
import {
  consumeAuthorOpenedFromHub,
  isAuthorOpenedFromHub,
  U153_AUTHOR_FROM_HUB,
} from '../hubEntryFlags';
import {
  AREAS,
  BASES,
  INDEXES,
  PERCENTILES,
  PROFILE_FIELD_GROUPS,
  PROFILE_FIELDS,
  QUARTILES,
  READINESS,
  REQUIREMENTS,
  SERVICE_OPTIONS,
  SUBJECTS,
} from '../authorRouteConstants';
import type { AuthorArticle } from '../types';

type AuthorView = 'HOME' | 'SERVICE' | 'READY' | 'PROFILE' | 'SEARCH' | 'SUCCESS';

type AuthorSlotStatus = 'FREE' | 'OCCUPIED' | 'TAKEN';
type InviteStatus = 'INVITED' | 'ACTIVATED' | 'METADATA' | 'CONFIRMED';

interface ServiceAuthor {
  id: string;
  name: string;
  email: string;
  affiliation: string;
  position: string;
  country: string;
  orcid: string;
  scopus: string;
  status: InviteStatus;
  self?: boolean;
}

const INVITE_STATUS_LABEL: Record<InviteStatus, string> = {
  INVITED: 'Приглашён',
  ACTIVATED: 'Активировал аккаунт',
  METADATA: 'Данные заполнены',
  CONFIRMED: 'Подтверждён',
};

function selfAuthor(name: string, email: string): ServiceAuthor {
  return {
    id: 'self',
    name,
    email,
    affiliation: '',
    position: '',
    country: 'Kazakhstan',
    orcid: '',
    scopus: '',
    status: 'CONFIRMED',
    self: true,
  };
}

function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function normalizeServiceAuthors(raw: unknown, name: string, email: string): ServiceAuthor[] {
  if (!Array.isArray(raw) || raw.length === 0) return [selfAuthor(name, email)];
  return raw.map((item, index) => {
    const row = (item && typeof item === 'object' ? item : {}) as Partial<ServiceAuthor>;
    const status = String(row.status || 'INVITED').toUpperCase();
    const allowed: InviteStatus[] = ['INVITED', 'ACTIVATED', 'METADATA', 'CONFIRMED'];
    return {
      id: String(row.id || `author-${index + 1}`),
      name: String(row.name || ''),
      email: String(row.email || ''),
      affiliation: String(row.affiliation || ''),
      position: String(row.position || ''),
      country: String(row.country || ''),
      orcid: String(row.orcid || ''),
      scopus: String(row.scopus || ''),
      status: (allowed.includes(status as InviteStatus) ? status : 'INVITED') as InviteStatus,
      self: Boolean(row.self) || index === 0,
    };
  });
}

interface AuthorSlotRow {
  position: number;
  role: string;
  name: string;
  status: AuthorSlotStatus;
}

function emptyAuthorSlots(): AuthorSlotRow[] {
  return Array.from({ length: 8 }, (_, index) => ({
    position: index + 1,
    role: '',
    name: '',
    status: 'FREE',
  }));
}

function normalizeAuthorSlots(raw: unknown): AuthorSlotRow[] {
  const base = emptyAuthorSlots();
  if (!Array.isArray(raw)) return base;
  raw.slice(0, 8).forEach((item, index) => {
    if (!item || typeof item !== 'object') return;
    const row = item as Partial<AuthorSlotRow> & { status?: string };
    const status = String(row.status || 'FREE').toUpperCase();
    base[index] = {
      position: Number(row.position) || index + 1,
      role: String(row.role || ''),
      name: String(row.name || ''),
      status: status === 'OCCUPIED' || status === 'TAKEN' ? 'OCCUPIED' : 'FREE',
    };
  });
  return base;
}

interface AuthorWorkflowOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
}

const HOME_ROUTES = [
  {
    key: 'SERVICE' as const,
    title: 'Запрос услуги / сопровождения',
    desc: 'Консультация, редактирование, перевод, подбор журнала или полный цикл.',
    color: '#49ddb7',
    icon: 'M9 3h6M10 3v6l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3M8 15h8',
  },
  {
    key: 'READY' as const,
    title: 'Готовые статьи — присоединиться',
    desc: 'Одобренные объявления о готовых рукописях, свободные авторские места и заявка на участие.',
    color: '#9e80ff',
    icon: 'M5 4h11l3 3v13H5V4zM16 4v4h4M8 12h8M8 16h6',
  },
  {
    key: 'PROFILE' as const,
    title: 'Данные автора',
    desc: 'Анкета автора для оформления рукописи, журнала и submission-файлов.',
    color: '#e7b13a',
    icon: 'M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zM4 21c1.5-4 4.5-6 8-6s6.5 2 8 6',
  },
  {
    key: 'SEARCH' as const,
    title: 'Подать заявку на поиск соавторов',
    desc: 'Создать объявление о рукописи, указать индексацию, требования и свободные места; публикация — после модерации владельца.',
    color: '#41d2ff',
    icon: 'M10 10a4 4 0 1 0-4-4 4 4 0 0 0 4 4zM2 20c1-3.2 3.2-5 6-5M16 11l4 4M15 15l1.5 1.5L21 12',
  },
];

function toggleMultiValue(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function profileCompletion(data: Record<string, unknown>): number {
  const filled = PROFILE_FIELDS.filter((f) => String(data[f.key] ?? '').trim()).length;
  const photo = data.photoName ? 1 : 0;
  return Math.round(((filled + photo) / (PROFILE_FIELDS.length + 1)) * 100);
}

export const AuthorWorkflowOverlay: React.FC<AuthorWorkflowOverlayProps> = ({
  isOpen,
  onClose,
  onBack,
}) => {
  const { user } = useAuth();
  const identityName = user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Автор';
  const identityEmail = user?.email || '';
  const identityId = user?.academiaId || '';

  const [view, setView] = useState<AuthorView>('HOME');
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerNotice, setComposerNotice] = useState<string | null>(null);
  const [serviceStep, setServiceStep] = useState(0);
  const [profileStep, setProfileStep] = useState(0);
  const [serviceData, setServiceData] = useState<Record<string, unknown>>({
    services: [],
    bases: [],
    areas: [],
    message: '',
    phone: '',
  });
  const [profileData, setProfileData] = useState<Record<string, unknown>>({});
  const [searchData, setSearchData] = useState<Record<string, unknown>>({
    subjects: [],
    indexes: [],
    requirements: [],
    submissionDate: '',
    authorSlots: emptyAuthorSlots(),
    percentile: 'Не принципиально',
    quartiles: [],
    readiness: 'Полный черновик',
    corresponding: '1',
  });

  const [articles, setArticles] = useState<AuthorArticle[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<AuthorArticle | null>(null);
  const [slotRole, setSlotRole] = useState('2-й соавтор / эмпирическая часть');
  const [notes, setNotes] = useState('');
  const [marketQuery, setMarketQuery] = useState('');
  const [marketIndex, setMarketIndex] = useState('ALL');
  const [reserving, setReserving] = useState(false);
  const [reservationMessage, setReservationMessage] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [successMeta, setSuccessMeta] = useState<{
    number: string;
    kind: string;
    status: string;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const persistDraft = useCallback(
    async (routeKey: 'SERVICE' | 'PROFILE' | 'SEARCH', step: number, data: Record<string, unknown>) => {
      try {
        await saveAuthorRouteDraft({ route_key: routeKey, step, data });
      } catch {
        /* keep local state */
      }
    },
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';

    Promise.all([
      fetchAuthorArticles().catch(() => [] as AuthorArticle[]),
      getAuthorRouteDraft('SERVICE').catch(() => null),
      getAuthorRouteDraft('PROFILE').catch(() => null),
      getAuthorRouteDraft('SEARCH').catch(() => null),
    ]).then(([arts, serviceDraft, profileDraft, searchDraft]) => {
      setArticles(Array.isArray(arts) ? arts : []);
      if (serviceDraft?.data) {
        setServiceData((prev) => ({ ...prev, ...serviceDraft.data }));
        setServiceStep(serviceDraft.step ?? 0);
      }
      if (profileDraft?.data) {
        setProfileData((prev) => ({
          ...prev,
          ...profileDraft.data,
          nameLocal: profileDraft.data.nameLocal || identityName,
          email: profileDraft.data.email || identityEmail,
        }));
        setProfileStep(profileDraft.step ?? 0);
      } else {
        setProfileData((prev) => ({
          ...prev,
          nameLocal: identityName,
          email: identityEmail,
        }));
      }
      if (searchDraft?.data) {
        setSearchData((prev) => ({
          ...prev,
          ...searchDraft.data,
          authorSlots: normalizeAuthorSlots(searchDraft.data.authorSlots),
        }));
      }
    });

    return () => {
      if (!document.querySelector('.rg-overlay')) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, identityEmail, identityName]);

  const openRoute = (route: Exclude<AuthorView, 'HOME' | 'SUCCESS'>) => {
    setView(route);
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const saveServiceDraft = async (step = serviceStep) => {
    await persistDraft('SERVICE', step, serviceData);
  };

  const saveProfileDraft = async (step = profileStep) => {
    const data = {
      ...profileData,
      nameLocal: identityName,
      email: identityEmail,
    };
    setProfileData(data);
    await persistDraft('PROFILE', step, data);
  };

  const saveSearchDraft = useCallback(
    async (data: Record<string, unknown> = searchData) => {
      await persistDraft('SEARCH', 0, data);
    },
    [persistDraft, searchData]
  );

  const updateSearchData = useCallback(
    (patch: Record<string, unknown>) => {
      setSearchData((prev) => {
        const next = { ...prev, ...patch };
        void saveSearchDraft(next);
        return next;
      });
    },
    [saveSearchDraft]
  );

  const goServiceStep = async (next: number) => {
    await saveServiceDraft(next);
    setServiceStep(next);
  };

  const goProfileStep = async (next: number) => {
    await saveProfileDraft(next);
    setProfileStep(next);
  };

  const handleBack = () => {
    setErrorMessage(null);

    if (view === 'HOME') {
      // Root of AU123 → Create Request Hub; keep mount + drafts for Continue.
      consumeAuthorOpenedFromHub();
      onBack();
      return;
    }

    if (view === 'SUCCESS') {
      setView('HOME');
      setSuccessMessage(null);
      return;
    }

    if (view === 'SERVICE') {
      if (composerOpen) {
        void saveServiceDraft(serviceStep);
        setComposerOpen(false);
        setComposerNotice(null);
        return;
      }
      if (serviceStep > 0) {
        void goServiceStep(serviceStep - 1);
        return;
      }
      void saveServiceDraft(0);
      setView('HOME');
      return;
    }

    if (view === 'PROFILE') {
      if (profileStep > 0) {
        void goProfileStep(profileStep - 1);
        return;
      }
      void saveProfileDraft(0);
      setView('HOME');
      return;
    }

    if (view === 'READY') {
      if (selectedArticle) {
        setSelectedArticle(null);
        setReservationMessage(null);
        return;
      }
      setView('HOME');
      return;
    }

    if (view === 'SEARCH') {
      void saveSearchDraft(searchData);
      setView('HOME');
      return;
    }

    setView('HOME');
  };

  const backLabel =
    view === 'HOME' ? '← Вернуться к выбору маршрута' : '← Назад';

  const chrome =
    view === 'SERVICE'
      ? {
          kicker: 'РАЗДЕЛ 1 · ПУБЛИКАЦИОННЫЙ ЗАПРОС',
          title: 'Запрос услуги и публикационное сопровождение',
          sub: `Раздел 1 · шаг ${serviceStep + 1} из 4`,
        }
      : view === 'READY'
        ? {
            kicker: 'READY ARTICLES · MODERATED',
            title: 'Готовые статьи',
            sub: 'Одобренные объявления · поиск соавторов',
          }
        : view === 'PROFILE'
          ? {
              kicker: 'РАЗДЕЛ 3 · AUTHOR PROFILE',
              title: 'Данные автора для оформления статьи',
              sub: `Раздел 3 · шаг ${profileStep + 1} из 6`,
            }
          : view === 'SEARCH'
            ? {
                kicker: 'CO-AUTHOR SEARCH · OWNER MODERATION',
                title: 'Подать заявку на поиск соавторов',
                sub: 'Автор / соавтор · модерация владельца перед публикацией',
              }
            : view === 'SUCCESS'
              ? {
                  kicker: 'SUCCESS',
                  title: 'Отправлено владельцу',
                  sub: 'Заявка сохранена в реестре владельца',
                }
              : {
                  kicker: 'ACADEMIA · AUTHOR / CO-AUTHOR',
                  title: 'Создать заявку',
                  sub: 'Выберите один из четырёх авторских маршрутов',
                };

  const submitService = async () => {
    const services = (serviceData.services as string[]) || [];
    if (!services.length) {
      setErrorMessage('Выберите хотя бы один вариант услуги');
      return;
    }
    setBusy(true);
    setErrorMessage(null);
    try {
      await saveServiceDraft(serviceStep);
      const res = await submitAuthorInquiry({
        route_type: 'SERVICE',
        title: services[0]?.slice(0, 120) || 'Запрос услуги / сопровождения',
        description: String(serviceData.message || ''),
        answers: {
          services: serviceData.services,
          bases: serviceData.bases,
          areas: serviceData.areas,
          message: serviceData.message,
          phone: serviceData.phone,
          registeredName: identityName,
          registeredEmail: identityEmail,
          academiaId: identityId,
          authorsKnown: serviceData.authorsKnown || 'no',
          manualOverride: Boolean(serviceData.manualOverride),
          authors: normalizeServiceAuthors(serviceData.authors, identityName, identityEmail),
        },
      });
      setSuccessMessage(`Заявка ${res.request_number} успешно отправлена`);
      setSuccessMeta({ number: res.request_number, kind: 'Запрос услуги', status: res.status || 'NEW' });
      setView('SUCCESS');
    } catch (err: any) {
      setErrorMessage(err.message || 'Ошибка отправки');
    } finally {
      setBusy(false);
    }
  };

  const submitProfile = async () => {
    setBusy(true);
    setErrorMessage(null);
    try {
      await saveProfileDraft(profileStep);
      const res = await submitAuthorInquiry({
        route_type: 'PROFILE',
        title: 'Анкета автора',
        description: String(profileData.message || ''),
        answers: { ...profileData, academiaId: identityId },
      });
      setSuccessMessage(`Анкета ${res.request_number} отправлена владельцу`);
      setSuccessMeta({ number: res.request_number, kind: 'Данные автора', status: res.status || 'NEW' });
      setView('SUCCESS');
    } catch (err: any) {
      setErrorMessage(err.message || 'Ошибка отправки');
    } finally {
      setBusy(false);
    }
  };

  const submitSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const title = String(searchData.title || '').trim();
    const abstract = String(searchData.abstract || '').trim();
    const subjects = (searchData.subjects as string[]) || [];
    const indexes = (searchData.indexes as string[]) || [];
    if (!title || !abstract || !subjects.length || !indexes.length || !searchData.submissionDate || !searchData.readiness) {
      setErrorMessage('Заполните название, аннотацию, области, индексацию, готовность и дату подачи');
      return;
    }
    if (!searchData.corresponding) {
      setErrorMessage('Отметьте автора-корреспондента');
      return;
    }
    setBusy(true);
    setErrorMessage(null);
    try {
      await saveSearchDraft(searchData);
      const res = await submitAuthorInquiry({
        route_type: 'COAUTHOR_SEARCH',
        title,
        description: abstract,
        answers: searchData,
      });
      setSuccessMessage(`Объявление ${res.request_number} отправлено на модерацию`);
      setSuccessMeta({
        number: res.request_number,
        kind: 'Поиск соавторов',
        status: res.status || 'NEW',
      });
      setView('SUCCESS');
    } catch (err: any) {
      setErrorMessage(err.message || 'Ошибка отправки');
    } finally {
      setBusy(false);
    }
  };

  const handleReserve = async () => {
    if (!selectedArticle) return;
    setReserving(true);
    setReservationMessage(null);
    try {
      const res = await reserveAuthorSeat(selectedArticle.id, slotRole, notes);
      setReservationMessage(res.message);
      setArticles((prev) =>
        prev.map((a) =>
          a.id === selectedArticle.id
            ? { ...a, slots_open: Math.max(0, a.slots_open - 1) }
            : a
        )
      );
    } catch (err: any) {
      setReservationMessage(err.message || 'Ошибка бронирования');
    } finally {
      setReserving(false);
    }
  };

  const serviceNav = [
    ['1.1', 'Вариант работы'],
    ['1.2', 'База / перечень'],
    ['1.3', 'Научная область'],
    ['1.4', 'Описание и контакты'],
  ];

  const renderCheckboxGrid = (
    name: string,
    options: readonly string[],
    selected: string[],
    onChange: (next: string[]) => void,
    other?: { label: string; value: string; onChange: (value: string) => void; active: boolean }
  ) => (
    <div className="rg-option-grid">
      {options.map((option, index) => (
        <label key={option} className={`rg-option${selected.includes(option) ? ' is-selected' : ''}`}>
          <span className="rg-option-num">{String(index + 1).padStart(2, '0')}</span>
          <span className="rg-option-copy">{option}</span>
          <input
            type="checkbox"
            name={name}
            checked={selected.includes(option)}
            onChange={() => onChange(toggleMultiValue(selected, option))}
          />
        </label>
      ))}
      {other ? (
        <>
          <label className={`rg-option${other.active ? ' is-selected' : ''}`}>
            <span className="rg-option-num">+</span>
            <span className="rg-option-copy">{other.label}</span>
            <input
              type="checkbox"
              checked={other.active}
              onChange={() => onChange(toggleMultiValue(selected, other.label))}
            />
          </label>
          {other.active ? (
            <input
              className="rg-option-other"
              value={other.value}
              placeholder="Введите свой вариант"
              onChange={(e) => other.onChange(e.target.value)}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );

  const renderServiceStep = () => {
    const services = (serviceData.services as string[]) || [];
    const bases = (serviceData.bases as string[]) || [];
    const areas = (serviceData.areas as string[]) || [];

    if (serviceStep === 0) {
      return (
        <section className="rg-panel rg-panel--sheet">
          <div className="rg-step-head">
            <h3>1.1 Какой вариант вам интересен?</h3>
            <p>Можно выбрать несколько вариантов. Каждый ответ оформлен отдельной карточкой.</p>
          </div>
          <div className="rg-panel-body">
          {renderCheckboxGrid('services', SERVICE_OPTIONS, services, (next) =>
            setServiceData((d) => ({ ...d, services: next })), {
              label: 'Другое / Басқа',
              value: String(serviceData.serviceOther || ''),
              active: services.includes('Другое / Басқа'),
              onChange: (value) => setServiceData((d) => ({ ...d, serviceOther: value })),
            }
          )}
          </div>
        </section>
      );
    }
    if (serviceStep === 1) {
      return (
        <section className="rg-panel rg-panel--sheet">
          <div className="rg-step-head">
            <h3>1.2 Какая база / перечень вам нужен?</h3>
          </div>
          <div className="rg-panel-body">
          {renderCheckboxGrid('bases', BASES, bases, (next) =>
            setServiceData((d) => ({ ...d, bases: next })), {
              label: 'Другое / Басқа',
              value: String(serviceData.baseOther || ''),
              active: bases.includes('Другое / Басқа'),
              onChange: (value) => setServiceData((d) => ({ ...d, baseOther: value })),
            }
          )}
          </div>
        </section>
      );
    }
    if (serviceStep === 2) {
      return (
        <section className="rg-panel rg-panel--sheet">
          <div className="rg-step-head">
            <h3>1.3 Предпочтительная область</h3>
          </div>
          <div className="rg-panel-body">
          {renderCheckboxGrid('areas', AREAS, areas, (next) =>
            setServiceData((d) => ({ ...d, areas: next })), {
              label: 'Другое / Басқа',
              value: String(serviceData.areaOther || ''),
              active: areas.includes('Другое / Басқа'),
              onChange: (value) => setServiceData((d) => ({ ...d, areaOther: value })),
            }
          )}
          </div>
        </section>
      );
    }
    return (
      <section className="rg-panel rg-panel--sheet">
        <div className="rg-step-head">
          <h3>1.4 Описание запроса и контакты</h3>
          <p>Опишите тему, примерное название, сроки и требования журнала, если они уже известны.</p>
        </div>
        <div className="rg-panel-body rg-field-grid">
          <div className="rg-span-2">
            <label>Дополнительная информация, сообщение координатору</label>
            <textarea
              rows={4}
              value={String(serviceData.message || '')}
              onChange={(e) => setServiceData((d) => ({ ...d, message: e.target.value }))}
              placeholder="Тема исследования, название статьи, сроки, требования..."
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label>Ф.И.О. из аккаунта</label>
              <input readOnly value={identityName} />
            </div>
            <div>
              <label>E-mail из аккаунта</label>
              <input readOnly value={identityEmail} />
            </div>
            <div>
              <label>Author ID</label>
              <input readOnly value={identityId} />
            </div>
            <div>
              <label>WhatsApp / телефон</label>
              <input
                value={String(serviceData.phone || '')}
                onChange={(e) => setServiceData((d) => ({ ...d, phone: e.target.value }))}
                placeholder="+7 700 123 45 67"
              />
            </div>
          </div>
        </div>
      </section>
    );
  };

  const profileGroups = useMemo(
    () => [
      ...PROFILE_FIELD_GROUPS.map((g) => ({
        title: g.title,
        desc: g.desc,
        fields: PROFILE_FIELDS.slice(g.start, g.end),
      })),
      {
        title: 'Фото и отправка',
        desc: 'Необязательное фото + финальная проверка',
        fields: [] as typeof PROFILE_FIELDS,
      },
    ],
    []
  );

  const renderProfileStep = () => {
    if (profileStep < 5) {
      const group = profileGroups[profileStep];
      return (
        <section className="rg-panel rg-panel--sheet">
          <div className="rg-step-head" style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <h3>{group.title}</h3>
              <p>{group.desc}</p>
            </div>
            <span className="rg-chip is-amber">{profileCompletion(profileData)}% заполнено</span>
          </div>
          <div className="rg-panel-body rg-field-grid">
            {group.fields.map((field) => (
              <div key={field.key} className={field.type === 'textarea' ? 'rg-span-2' : ''}>
                <label>{field.label}</label>
                {field.type === 'textarea' ? (
                  <textarea
                    rows={3}
                    value={String(profileData[field.key] ?? '')}
                    placeholder={field.placeholder}
                    readOnly={field.key === 'nameLocal' || field.key === 'email'}
                    onChange={(e) =>
                      setProfileData((d) => ({ ...d, [field.key]: e.target.value }))
                    }
                  />
                ) : (
                  <input
                    type={field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'}
                    value={String(profileData[field.key] ?? '')}
                    placeholder={field.placeholder}
                    readOnly={field.key === 'nameLocal' || field.key === 'email'}
                    onChange={(e) =>
                      setProfileData((d) => ({ ...d, [field.key]: e.target.value }))
                    }
                  />
                )}
                {field.help ? (
                  <small style={{ display: 'block', marginTop: 6, color: '#86a6b6' }}>{field.help}</small>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      );
    }

    return (
      <>
        <section className="rg-panel">
          <div className="rg-step-head">
            <h3>3.24 Фото автора / файл с фотографией</h3>
            <p>Необязательный дополнительный материал для журналов с биографией автора.</p>
          </div>
          <input
            type="file"
            accept="image/*,.pdf,.doc,.docx"
            onChange={(e) => {
              const file = e.target.files?.[0];
              setProfileData((d) => ({
                ...d,
                photoName: file?.name || '',
              }));
            }}
          />
          {profileData.photoName ? (
            <p style={{ marginTop: 8, color: '#86a6b6', fontSize: 12 }}>
              Выбрано: {String(profileData.photoName)}
            </p>
          ) : null}
        </section>
        <section className="rg-panel">
          <div className="rg-step-head">
            <h3>Проверка анкеты</h3>
            <p>Заполнено: {profileCompletion(profileData)}%</p>
          </div>
        </section>
      </>
    );
  };

  const renderSearchForm = () => {
    const subjects = (searchData.subjects as string[]) || [];
    const indexes = (searchData.indexes as string[]) || [];
    const requirements = (searchData.requirements as string[]) || [];
    const authorSlots = normalizeAuthorSlots(searchData.authorSlots);

    const updateSlot = (index: number, patch: Partial<AuthorSlotRow>) => {
      const next = authorSlots.map((slot, i) => (i === index ? { ...slot, ...patch } : slot));
      updateSearchData({ authorSlots: next });
    };

    return (
      <form onSubmit={submitSearch} className="space-y-4">
        <section className="rg-panel">
          <div className="rg-step-head">
            <h3>1. Рукопись и научная область</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label>Рабочее название статьи *</label>
              <input
                required
                value={String(searchData.title || '')}
                onChange={(e) => updateSearchData({ title: e.target.value })}
              />
            </div>
            <div>
              <label>Предметная область *</label>
              {renderCheckboxGrid('subjects', SUBJECTS, subjects, (next) =>
                updateSearchData({ subjects: next })
              )}
              {subjects.includes('Другое') ? (
                <input
                  style={{ marginTop: 10 }}
                  value={String(searchData.subjectOther || '')}
                  placeholder="Укажите свою область"
                  onChange={(e) => updateSearchData({ subjectOther: e.target.value })}
                />
              ) : null}
            </div>
            <div>
              <label>Аннотация / краткое описание *</label>
              <textarea
                required
                rows={4}
                value={String(searchData.abstract || '')}
                onChange={(e) => updateSearchData({ abstract: e.target.value })}
              />
            </div>
          </div>
        </section>

        <section className="rg-panel">
          <div className="rg-step-head">
            <h3>2. Индексация и готовность</h3>
          </div>
          <div className="space-y-4">
            <div>
              <label>Целевая индексация / формат *</label>
              {renderCheckboxGrid('indexes', INDEXES, indexes, (next) =>
                updateSearchData({ indexes: next })
              )}
              {indexes.includes('Другой индекс / перечень') ? (
                <input
                  style={{ marginTop: 10 }}
                  value={String(searchData.indexOther || '')}
                  placeholder="Другой индекс / перечень"
                  onChange={(e) => updateSearchData({ indexOther: e.target.value })}
                />
              ) : null}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label>Scopus percentile</label>
                <select
                  value={String(searchData.percentile || 'Не принципиально')}
                  onChange={(e) => updateSearchData({ percentile: e.target.value })}
                >
                  {PERCENTILES.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </div>
              <div>
                <label>WoS / квартиль</label>
                {renderCheckboxGrid('quartiles', QUARTILES, (searchData.quartiles as string[]) || [], (next) =>
                  updateSearchData({ quartiles: next })
                )}
              </div>
              <div>
                <label>Готовность рукописи *</label>
                <select
                  required
                  value={String(searchData.readiness || '')}
                  onChange={(e) => updateSearchData({ readiness: e.target.value })}
                >
                  {READINESS.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </section>

        <section className="rg-panel">
          <div className="rg-step-head">
            <h3>3. Требования к соавторам</h3>
          </div>
          {renderCheckboxGrid('requirements', REQUIREMENTS, requirements, (next) =>
            updateSearchData({ requirements: next })
          )}
          <div style={{ marginTop: 14 }}>
            <label>Свои требования / ожидаемый вклад</label>
            <textarea
              rows={3}
              value={String(searchData.requirementsOther || '')}
              placeholder="Например: Discussion, статистика, корпус, перевод, funding statement…"
              onChange={(e) => updateSearchData({ requirementsOther: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ marginTop: 14 }}>
            <div>
              <label>Ориентировочная дата подачи *</label>
              <input
                type="date"
                required
                value={String(searchData.submissionDate || '')}
                onChange={(e) => updateSearchData({ submissionDate: e.target.value })}
              />
            </div>
            <div>
              <label>Комментарий потенциальным соавторам</label>
              <input
                value={String(searchData.note || '')}
                placeholder="Условия взаимодействия / дедлайн ответа"
                onChange={(e) => updateSearchData({ note: e.target.value })}
              />
            </div>
          </div>
        </section>

        <section className="rg-panel">
          <div className="rg-step-head">
            <h3>4. Авторский состав</h3>
            <p>Для каждого из восьми мест укажите статус. Один автор обязательно отмечается как corresponding author.</p>
          </div>
          <div className="space-y-3">
            {authorSlots.map((slot, index) => (
              <div key={slot.position} className="rg-team-row" style={{ flexWrap: 'wrap', alignItems: 'end' }}>
                <strong style={{ minWidth: 88 }}>{slot.position}-е место</strong>
                <div style={{ flex: '1 1 160px' }}>
                  <label>Статус</label>
                  <select
                    value={slot.status === 'TAKEN' ? 'OCCUPIED' : slot.status}
                    onChange={(e) => updateSlot(index, { status: e.target.value as AuthorSlotStatus })}
                  >
                    <option value="FREE">Свободно</option>
                    <option value="OCCUPIED">Занято</option>
                  </select>
                </div>
                <div style={{ flex: '2 1 220px' }}>
                  <label>Кем занято / примечание</label>
                  <input
                    value={slot.name}
                    placeholder="Ф.И.О. или оставить пустым"
                    onChange={(e) => updateSlot(index, { name: e.target.value })}
                  />
                </div>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="radio"
                    name="corresponding"
                    checked={String(searchData.corresponding) === String(slot.position)}
                    onChange={() => {
                      const next = authorSlots.map((row, rowIndex) => ({
                        ...row,
                        role: rowIndex === index ? 'corresponding author' : row.role === 'corresponding author' ? '' : row.role,
                      }));
                      updateSearchData({ corresponding: String(slot.position), authorSlots: next });
                    }}
                  />
                  <span>Автор-корреспондент</span>
                </label>
              </div>
            ))}
          </div>
        </section>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="rg-btn" onClick={handleBack}>
            ← Назад
          </button>
          <button type="submit" disabled={busy} className="rg-btn primary">
            {busy ? 'Отправка…' : 'Отправить владельцу на модерацию →'}
          </button>
        </div>
      </form>
    );
  };

  const serviceAuthors = normalizeServiceAuthors(serviceData.authors, identityName, identityEmail);
  const authorsKnown = String(serviceData.authorsKnown || 'no') === 'yes';
  const visibleAuthors = authorsKnown ? serviceAuthors : serviceAuthors.filter((row) => row.self).slice(0, 1);
  const composerReady =
    Boolean(serviceData.manualOverride) || visibleAuthors.every((row) => row.status === 'CONFIRMED');

  const writeAuthors = (next: ServiceAuthor[], patch: Record<string, unknown> = {}) => {
    setServiceData((prev) => ({ ...prev, authors: next, ...patch }));
  };

  const patchAuthor = (id: string, patch: Partial<ServiceAuthor>) => {
    writeAuthors(serviceAuthors.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const moveAuthor = (id: string, direction: -1 | 1) => {
    const list = [...serviceAuthors];
    const index = list.findIndex((row) => row.id === id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= list.length) return;
    if (list[index].self || list[target].self) return;
    const [item] = list.splice(index, 1);
    list.splice(target, 0, item);
    writeAuthors(list);
  };

  const renderComposer = () => (
    <section className="rg-panel rg-panel--sheet">
      <div className="rg-step-head">
        <h3>Авторский состав</h3>
        <p>Только для запроса услуги и публикационного сопровождения. Неизвестные факты не подставляются.</p>
      </div>
      <div className="rg-panel-body" style={{ display: 'grid', gap: 14 }}>
        <div>
          <strong>Авторы уже известны?</strong>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <button
              type="button"
              className={`rg-btn${authorsKnown ? '' : ' primary'}`}
              onClick={() => writeAuthors(serviceAuthors, { authorsKnown: 'no' })}
            >
              Нет — только мои данные
            </button>
            <button
              type="button"
              className={`rg-btn${authorsKnown ? ' primary' : ''}`}
              onClick={() => writeAuthors(serviceAuthors, { authorsKnown: 'yes' })}
            >
              Да — добавить соавторов
            </button>
          </div>
          <p style={{ color: '#9bb7c6', marginBottom: 0 }}>
            Если «Нет», текущий аккаунт остаётся единственным известным профилем. Если «Да», открывается приглашение соавторов.
          </p>
        </div>

        {visibleAuthors.map((author, index) => (
          <article key={author.id} className="rg-team-row" style={{ alignItems: 'stretch' }}>
            <strong style={{ minWidth: 28 }}>{index + 1}</strong>
            <div style={{ flex: 1, display: 'grid', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <b>{author.name || author.email || 'Новый автор'}</b>
                <span className={`rg-chip${author.status === 'CONFIRMED' ? ' is-amber' : ''}`}>
                  {INVITE_STATUS_LABEL[author.status]}
                </span>
                {author.self ? <span className="rg-chip">Текущий пользователь</span> : null}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input
                  value={author.name}
                  readOnly={author.self}
                  placeholder="Ф.И.О."
                  onChange={(e) => patchAuthor(author.id, { name: e.target.value })}
                />
                <input
                  value={author.email}
                  readOnly={author.self}
                  placeholder="E-mail"
                  onChange={(e) => patchAuthor(author.id, { email: e.target.value, status: author.self ? author.status : 'INVITED' })}
                />
                <input
                  value={author.affiliation}
                  placeholder="Аффилиация"
                  onChange={(e) => patchAuthor(author.id, { affiliation: e.target.value })}
                />
                <input
                  value={author.position}
                  placeholder="Должность"
                  onChange={(e) => patchAuthor(author.id, { position: e.target.value })}
                />
                <input
                  value={author.country}
                  placeholder="Страна"
                  onChange={(e) => patchAuthor(author.id, { country: e.target.value })}
                />
                <input
                  value={author.orcid}
                  placeholder="ORCID"
                  onChange={(e) => patchAuthor(author.id, { orcid: e.target.value })}
                />
              </div>
              {!author.self ? (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button type="button" className="rg-btn" onClick={() => moveAuthor(author.id, -1)}>↑</button>
                  <button type="button" className="rg-btn" onClick={() => moveAuthor(author.id, 1)}>↓</button>
                  <button
                    type="button"
                    className="rg-btn"
                    onClick={() => {
                      if (!isEmail(author.email)) {
                        setComposerNotice('Проверьте формат e-mail. Доставляемость подтверждает почтовый сервис.');
                        return;
                      }
                      setComposerNotice(`Формат ${author.email} принят. Статус остаётся «Приглашён», пока автор не активирует приглашение.`);
                    }}
                  >
                    Проверить e-mail
                  </button>
                  <button type="button" className="rg-btn" onClick={() => patchAuthor(author.id, { status: 'INVITED' })}>
                    Пригласить
                  </button>
                  {author.status === 'INVITED' ? (
                    <button type="button" className="rg-btn" onClick={() => patchAuthor(author.id, { status: 'ACTIVATED' })}>
                      Открыть приглашение
                    </button>
                  ) : null}
                  {author.status === 'ACTIVATED' || author.status === 'METADATA' ? (
                    <button
                      type="button"
                      className="rg-btn"
                      onClick={() => {
                        if (!author.name.trim() || !author.affiliation.trim() || !author.position.trim() || !author.country.trim() || !isEmail(author.email)) {
                          setComposerNotice('Для статуса «Данные заполнены» нужны ФИО, аффилиация, должность, страна и e-mail.');
                          return;
                        }
                        patchAuthor(author.id, { status: 'METADATA' });
                        setComposerNotice(null);
                      }}
                    >
                      Данные автора
                    </button>
                  ) : null}
                  {author.status === 'METADATA' ? (
                    <button type="button" className="rg-btn primary" onClick={() => patchAuthor(author.id, { status: 'CONFIRMED' })}>
                      Подтвердить
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="rg-btn"
                    onClick={() => writeAuthors(serviceAuthors.filter((row) => row.id !== author.id))}
                  >
                    Удалить
                  </button>
                </div>
              ) : null}
            </div>
          </article>
        ))}

        {authorsKnown ? (
          <button
            type="button"
            className="rg-btn"
            onClick={() =>
              writeAuthors([
                ...serviceAuthors,
                {
                  id: `author-${Date.now()}`,
                  name: '',
                  email: '',
                  affiliation: '',
                  position: '',
                  country: '',
                  orcid: '',
                  scopus: '',
                  status: 'INVITED',
                },
              ])
            }
          >
            + Добавить автора
          </button>
        ) : null}

        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            checked={Boolean(serviceData.manualOverride)}
            onChange={(e) => setServiceData((prev) => ({ ...prev, manualOverride: e.target.checked }))}
          />
          <span>
            <strong>Продолжить без подтверждения</strong>
            <br />
            <small>Я заполню недостающие данные авторов вручную.</small>
          </span>
        </label>
        {composerNotice ? <p style={{ color: '#e7b13a', margin: 0 }}>{composerNotice}</p> : null}
        <button
          type="button"
          className="rg-btn primary"
          disabled={!composerReady}
          onClick={() => {
            void saveServiceDraft(serviceStep);
            setComposerOpen(false);
            setComposerNotice(null);
          }}
        >
          {composerReady ? 'Состав сохранён — вернуться к форме' : 'Ожидается подтверждение авторов'}
        </button>
      </div>
    </section>
  );

  if (!isOpen) return null;

  return (
    <div className="rg-overlay rg-wizard" role="dialog" aria-modal="true" aria-label="Авторский маршрут">
      <div className="rg-shell rg-wizard-shell">
        <div className="rg-top">
          <div className="rg-top-left">
            <button
              type="button"
              onClick={handleBack}
              className="rg-btn"
              data-rg-back-handler={view === 'HOME' ? 'U153_AUTHOR_TO_HUB' : 'AU123_STACK'}
              data-u153-author-from-hub={
                view === 'HOME' && isAuthorOpenedFromHub() ? '1' : undefined
              }
              aria-description={
                view === 'HOME'
                  ? `Return to Create Request Hub (${U153_AUTHOR_FROM_HUB})`
                  : undefined
              }
            >
              {backLabel}
            </button>
            <div>
              <small>{chrome.kicker}</small>
              <h2>{chrome.title}</h2>
              <p>{chrome.sub}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rg-btn">
            Закрыть ×
          </button>
        </div>

        <div className="rg-wizard-body">
          {errorMessage ? (
            <div className="rg-panel" style={{ marginBottom: 14, borderColor: 'rgba(255,123,101,0.45)', color: '#ff7b65' }}>
              {errorMessage}
            </div>
          ) : null}

          {view === 'HOME' && (
            <>
              <section className="rg-author-hero">
                <div>
                  <small>Кабинет автора и соавтора</small>
                  <h3>Новая заявка</h3>
                  <p>
                    Выберите нужный сценарий. Первые три маршрута сохранены; четвёртый позволяет подать
                    объявление на поиск соавторов. Имя, почта и номер автора берутся из регистрации
                    автоматически.
                  </p>
                </div>
                <aside className="rg-author-identity">
                  <strong>{identityName}</strong>
                  <span>{identityEmail}</span>
                  <span>ID автора: {identityId || '—'}</span>
                  <b>Данные из профиля</b>
                </aside>
              </section>
              <div className="rg-route-home-grid rg-route-home-grid--author">
                {HOME_ROUTES.map((route, index) => (
                  <button
                    key={route.key}
                    type="button"
                    className="rg-route-home-card rg-route-home-card--xl"
                    style={{ '--rg-accent': route.color } as React.CSSProperties}
                    onClick={() => openRoute(route.key)}
                    data-au123-route={route.key}
                  >
                    <span className="rg-route-home-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24">
                        <path d={route.icon} />
                      </svg>
                    </span>
                    <small className="rg-route-home-num">0{index + 1}</small>
                    <h4>{route.title}</h4>
                    <p>{route.desc}</p>
                    <span>Открыть маршрут →</span>
                  </button>
                ))}
              </div>
            </>
          )}

          {view === 'SERVICE' && (
            <>
              <section className="rg-author-hero">
                <div>
                  <small>РАЗДЕЛ 1 · ПУБЛИКАЦИОННЫЙ ЗАПРОС</small>
                  <h3>Запрос услуги / сопровождения</h3>
                  <p>
                    Форма разбита на четыре последовательных шага. Черновик сохраняется и его можно
                    изменить позже.
                  </p>
                </div>
                <aside className="rg-author-identity">
                  <strong>{identityName}</strong>
                  <span>{identityEmail}</span>
                  <span>ID автора: {identityId || '—'}</span>
                  <b>Данные из профиля</b>
                </aside>
              </section>
              <nav className="rg-service-nav">
                {serviceNav.map(([num, label], index) => (
                  <button
                    key={num}
                    type="button"
                    className={`rg-wizard-step${serviceStep === index ? ' is-active' : ''}`}
                    onClick={() => goServiceStep(index)}
                  >
                    <small>{num}</small>
                    <strong>{label}</strong>
                  </button>
                ))}
              </nav>
              <section className="rg-panel" style={{ marginBottom: 14 }}>
                <div className="rg-step-head">
                  <h3>Авторский состав</h3>
                  <p>Укажите известных авторов, порядок и подтверждение приглашений. Блок относится только к запросу услуги.</p>
                </div>
                <button type="button" className="rg-btn primary" onClick={() => setComposerOpen(true)}>
                  Открыть авторский состав
                </button>
              </section>
              {composerOpen ? renderComposer() : renderServiceStep()}
              {composerOpen ? null : (
              <div className="rg-etalon-actions">
                <button type="button" className="rg-btn" onClick={handleBack}>
                  ← Назад
                </button>
                <button type="button" className="rg-btn" onClick={() => saveServiceDraft()}>
                  Сохранить черновик
                </button>
                <span style={{ flex: 1 }} />
                {serviceStep < 3 ? (
                  <button type="button" className="rg-btn primary" onClick={() => goServiceStep(serviceStep + 1)}>
                    Далее →
                  </button>
                ) : (
                  <button type="button" disabled={busy} className="rg-btn primary" onClick={submitService}>
                    {busy ? 'Отправка…' : 'Отправить владельцу →'}
                  </button>
                )}
              </div>
              )}
            </>
          )}

          {view === 'READY' && (
            <>
              <section className="rg-author-hero rg-market-hero">
                <div>
                  <small>ПОДБОР СОАВТОРОВ</small>
                  <h3>Готовые статьи и свободные авторские места</h3>
                  <p>Здесь только одобренные объявления. Выберите рукопись и отправьте заявку на свободное место.</p>
                </div>
              </section>
              <div className="rg-market-toolbar">
                <input
                  value={marketQuery}
                  placeholder="Поиск по теме, журналу или области"
                  onChange={(e) => setMarketQuery(e.target.value)}
                />
                <select value={marketIndex} onChange={(e) => setMarketIndex(e.target.value)}>
                  <option value="ALL">Все базы / форматы</option>
                  {INDEXES.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </div>
              <div className="rg-market-grid">
                {articles
                  .filter((art) => {
                    const hay = `${art.title} ${art.journal_name} ${art.discipline} ${art.indexing}`.toLowerCase();
                    const queryOk = !marketQuery.trim() || hay.includes(marketQuery.trim().toLowerCase());
                    const indexOk = marketIndex === 'ALL' || art.indexing.includes(marketIndex);
                    return queryOk && indexOk;
                  })
                  .map((art) => (
                  <article key={art.id} className="rg-article-card">
                    <span className="rg-route-home-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24"><path d="M5 4h11l3 3v13H5V4zM16 4v4h4M8 12h8M8 16h6" /></svg>
                    </span>
                    <div>
                      <h4>{art.title}</h4>
                      <div className="rg-chip-row">
                        <span className="rg-chip">{art.quartile} · {art.indexing}</span>
                        <span className="rg-chip">{art.discipline}</span>
                        <span className="rg-chip">{art.journal_name}</span>
                      </div>
                      <p>{art.abstract_text}</p>
                    </div>
                    <div className="rg-article-foot">
                      <span>Свободно: {art.slots_open} из {art.slots_total} · до {art.deadline}</span>
                      <button
                        type="button"
                        disabled={art.slots_open <= 0}
                        onClick={() => {
                          setSelectedArticle(art);
                          setReservationMessage(null);
                        }}
                        className="rg-btn primary"
                      >
                        {art.slots_open > 0 ? 'Забронировать место →' : 'Все места заняты'}
                      </button>
                    </div>
                  </article>
                ))}
              </div>

              {selectedArticle ? (
                <div className="rg-panel" style={{ marginTop: 16 }}>
                  <div className="rg-step-head">
                    <h3>Бронирование места соавтора</h3>
                    <p>{selectedArticle.title}</p>
                  </div>
                  {reservationMessage ? (
                    <div className="rg-panel" style={{ marginBottom: 14, borderColor: 'rgba(73,221,183,0.45)', color: '#49ddb7' }}>
                      {reservationMessage}
                    </div>
                  ) : null}
                  <div style={{ display: 'grid', gap: 14 }}>
                    <div>
                      <label>Предполагаемая роль в исследовании</label>
                      <select value={slotRole} onChange={(e) => setSlotRole(e.target.value)}>
                        <option value="2-й соавтор / эмпирическая часть">2-й соавтор / сбор и анализ эмпирических данных</option>
                        <option value="3-й соавтор / статистический анализ">3-й соавтор / математико-статистический анализ</option>
                        <option value="Соавтор / обзор литературы">Соавтор / теоретический обзор и концепция</option>
                        <option value="Соавтор / академический перевод">Соавтор / перевод и адаптация под журнал</option>
                      </select>
                    </div>
                    <div>
                      <label>Краткая информация о вашем вкладе</label>
                      <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                    <button type="button" className="rg-btn" onClick={handleBack}>
                      ← Назад
                    </button>
                    <button type="button" disabled={reserving} className="rg-btn primary" onClick={handleReserve}>
                      {reserving ? 'Бронирование…' : 'Подтвердить бронирование'}
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          )}

          {view === 'PROFILE' && (
            <>
            <section className="rg-author-hero">
              <div>
                <small>РАЗДЕЛ 3 · AUTHOR PROFILE</small>
                <h3>Анкета автора</h3>
                <p>
                  Поля повторяют анкету для оформления автора в рукописи и submission-файлах.
                  Зарегистрированные Ф.И.О. и e-mail подставляются автоматически.
                </p>
              </div>
              <aside className="rg-author-identity">
                <strong>{identityName}</strong>
                <span>{identityEmail}</span>
                <span>ID автора: {identityId || '—'}</span>
                <b>Данные из профиля</b>
              </aside>
            </section>
            <div className="rg-profile-layout">
              <nav className="rg-wizard-steps rg-profile-steps">
                {profileGroups.map((group, index) => (
                  <button
                    key={group.title}
                    type="button"
                    className={`rg-wizard-step${profileStep === index ? ' is-active' : ''}`}
                    onClick={() => goProfileStep(index)}
                  >
                    <small>{index + 1}</small>
                    <strong>{group.title}</strong>
                  </button>
                ))}
              </nav>
              <div className="rg-profile-main">
              {renderProfileStep()}
              <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                <button type="button" className="rg-btn" onClick={handleBack}>
                  ← Назад
                </button>
                <button type="button" className="rg-btn" onClick={() => saveProfileDraft()}>
                  Сохранить
                </button>
                <span style={{ flex: 1 }} />
                {profileStep < 5 ? (
                  <button type="button" className="rg-btn primary" onClick={() => goProfileStep(profileStep + 1)}>
                    Далее →
                  </button>
                ) : (
                  <button type="button" disabled={busy} className="rg-btn primary" onClick={submitProfile}>
                    {busy ? 'Отправка…' : 'Отправить владельцу →'}
                  </button>
                )}
              </div>
              </div>
            </div>
            </>
          )}

          {view === 'SEARCH' && (
            <>
              <section className="rg-author-hero rg-market-hero">
                <div>
                  <small>ПОИСК СОАВТОРОВ</small>
                  <h3>Объявление о поиске соавторов</h3>
                  <p>После отправки объявление увидит владелец системы; публикация — после одобрения.</p>
                </div>
              </section>
              {renderSearchForm()}
            </>
          )}

          {view === 'SUCCESS' && successMessage ? (
            <section className="rg-author-hero">
              <div>
                <small>Готово · {successMeta?.number}</small>
                <h3>{successMeta?.kind || 'Заявка'} отправлены</h3>
                <p>
                  Заявка поступила владельцу системы. Author ID закреплён за зарегистрированным e-mail.
                  Статус: {successMeta?.status === 'NEW' ? 'НОВОЕ' : successMeta?.status || 'НОВОЕ'}.
                </p>
                <p>{successMessage}</p>
                <button type="button" className="rg-btn primary" onClick={() => setView('HOME')}>
                  Создать ещё заявку
                </button>
              </div>
              <aside className="rg-author-identity">
                <strong>{identityId || '—'}</strong>
                <span>{identityEmail}</span>
                <b>ВАШ ПОСТОЯННЫЙ AUTHOR ID</b>
              </aside>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
};
